if (!customElements.get('service-body-deposit')) {
  customElements.define('service-body-deposit', class extends HTMLElement {
    connectedCallback() {
      this.nativeForm = document.getElementById(this.dataset.productForm);
      this.trigger = this.nativeForm?.querySelector('[data-service-body-preorder]');
      if (!this.status) {
        this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        this.status = this.querySelector('[data-deposit-status]');
        this.reference = this.querySelector('[data-deposit-reference]');
        this.newCheckout = this.querySelector('[data-deposit-new-checkout]');
        this.storageKey = `service-body-checkout:v1:${this.config.bundleHandle}`;
        this.initialLabel = this.trigger?.textContent;
        if (this.dataset.endpoint) {
          try {
            const endpoint = new URL(this.dataset.endpoint, window.location.origin);
            if (endpoint.protocol !== 'https:' && !(endpoint.protocol === 'http:' && endpoint.hostname === 'localhost')) throw new Error('Invalid endpoint');
            const probe = `${this.storageKey}:probe`;
            sessionStorage.setItem(probe, '1');
            sessionStorage.removeItem(probe);
            this.attempt = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
            if (this.attempt && !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(this.attempt.id)) throw new Error('Invalid saved request');
            if (!crypto.randomUUID) throw new Error('Secure browser required');
            this.endpoint = endpoint;
          } catch (_) {
            this.status.textContent = this.config.messages.storage_error;
          }
        }
      }
      if (this.attempt) {
        try {
          if (this.attempt.canEdit !== true && !JSON.parse(this.attempt.selection).vehicle && this.trigger) this.trigger.dataset.resumeLegacy = 'true';
        } catch (_) {}
      }
      this.nativeForm?.querySelector('service-body-vehicle')?.sync();
      this.startHandler ||= this.start.bind(this);
      this.restartHandler ||= this.restart.bind(this);
      this.newCheckout?.addEventListener('click', this.restartHandler);
      if (this.trigger) {
        this.trigger.addEventListener('click', this.startHandler);
        this.nativeForm.addEventListener('submit', this.startHandler, { capture: true });
      }
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.startHandler);
      this.nativeForm?.removeEventListener('submit', this.startHandler, { capture: true });
      this.newCheckout?.removeEventListener('click', this.restartHandler);
    }

    payload() {
      const quantityInput = this.nativeForm?.elements.namedItem('quantity');
      const quantity = Number(quantityInput?.value || 1);
      if (!this.nativeForm || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 10 || (quantityInput && !quantityInput.checkValidity())) {
        throw new Error('selection_error');
      }
      const legacy = this.attempt && this.attempt.canEdit !== true && !JSON.parse(this.attempt.selection).vehicle;
      const selector = this.nativeForm.querySelector('service-body-vehicle');
      const vehicleChosen = selector && [selector.make, selector.model, selector.year].some(control => control?.value);
      const vehicle = vehicleChosen ? selector.selection() : null;
      if (!legacy && !vehicle) throw new Error('selection_error');
      const trundle = this.nativeForm.querySelector('[data-extras-trundle]')?.value || '1000';
      const waterTank = this.nativeForm.querySelector('[data-extras-water]')?.value === 'true';
      const wheelGuards = this.nativeForm.querySelector('[data-extras-wheel-guards]')?.value || 'standard';
      if (!['standard', 'wide'].includes(wheelGuards)) throw new Error('selection_error');
      if (!['1000', '1500'].includes(trundle) || (trundle === '1500' && waterTank)) throw new Error('selection_error');
      const saved = this.attempt && JSON.parse(this.attempt.selection);
      const includeExtras = !this.attempt || saved.extras !== undefined || trundle !== '1000' || waterTank || wheelGuards !== 'standard';
      const includeWheelGuards = !this.attempt || saved?.extras?.wheelGuards !== undefined || wheelGuards !== 'standard';
      if (includeExtras && !vehicle) throw new Error('selection_error');
      return { bundleHandle: this.config.bundleHandle, quantity, ...(vehicle ? { vehicle } : {}),
        ...(includeExtras ? { extras: { trundle, waterTank, ...(includeWheelGuards ? { wheelGuards } : {}) } } : {}) };
    }

    saveAttempt() {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.attempt));
    }

    async restart(event) {
      event.preventDefault();
      if (this.busy || this.attempt?.status !== 'checkout_ready') return;
      try {
        const payload = this.payload();
        if (!payload.vehicle) throw new Error('selection_error');
        const next = { id: crypto.randomUUID(), selection: JSON.stringify(payload), canEdit: true };
        sessionStorage.setItem(`${this.storageKey}:previous:${this.attempt.id}`, JSON.stringify(this.attempt));
        sessionStorage.setItem(this.storageKey, JSON.stringify(next));
        this.attempt = next;
        if (this.trigger) delete this.trigger.dataset.resumeLegacy;
        this.newCheckout.hidden = true;
      } catch (error) {
        this.status.textContent = this.config.messages[error.message] || this.config.messages.storage_error;
        this.status.focus();
        return;
      }
      return this.start(event);
    }

    checkoutUrl(value) {
      const url = new URL(value);
      const hosts = ['autospec4x4.com.au', 'www.autospec4x4.com.au', 'autospec-group.myshopify.com'];
      if (url.protocol !== 'https:' || !hosts.includes(url.hostname) || url.username || url.password || url.port || !/^\/(?:checkouts\/|(?:[0-9]+\/)?invoices?\/)/.test(url.pathname)) {
        throw new Error('network_error');
      }
      return url.href;
    }

    async start(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      this.hidden = false;
      const error = this.nativeForm?.querySelector('.js-form-error');
      if (error) error.hidden = true;
      if (!this.endpoint) {
        this.status.textContent ||= this.config.messages.unavailable;
        this.status.focus();
        return;
      }
      if (this.attempt?.status === 'review_required') {
        this.showResult(this.attempt);
        return;
      }
      this.busy = true;
      if (this.trigger) {
        this.trigger.disabled = true;
        this.trigger.textContent = this.config.messages.checking;
        this.trigger.setAttribute('aria-busy', 'true');
      }
      this.status.textContent = this.config.messages.checking;
      let timer;
      try {
        const payload = this.payload();
        const selection = JSON.stringify(payload);
        if (this.attempt && this.attempt.selection !== selection && this.attempt.canEdit !== true) throw new Error('changed_error');
        if (!this.attempt) this.attempt = { id: crypto.randomUUID(), selection };
        this.attempt.selection = selection;
        this.attempt.canEdit = false;
        try { this.saveAttempt(); } catch (_) { throw new Error('storage_error'); }
        const controller = new AbortController();
        timer = window.setTimeout(() => controller.abort(), 30000);
        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        if (this.dataset.previewKey) headers['X-Service-Body-Preview'] = this.dataset.previewKey;
        const response = await fetch(this.endpoint.href, {
          method: 'POST',
          headers,
          credentials: 'omit',
          body: JSON.stringify({ submissionId: this.attempt.id, ...payload }),
          signal: controller.signal
        });
        const result = await response.json();
        if (!result || !['checkout_ready', 'processing', 'review_required', 'error'].includes(result.status)) throw new Error('network_error');
        const target = result.status === 'checkout_ready' ? this.checkoutUrl(result.checkoutUrl) : null;
        this.attempt.status = result.status;
        this.attempt.reference = typeof result.reference === 'string' ? result.reference : this.attempt.id;
        this.attempt.canEdit = result.status === 'error' && result.canEdit === true && result.retryAllowed === true;
        this.saveAttempt();
        if (this.attempt.canEdit && this.trigger) delete this.trigger.dataset.resumeLegacy;
        this.showResult(result);
        if (target) {
          if (JSON.stringify(this.payload()) !== this.attempt.selection) throw new Error('changed_error');
          window.location.assign(target);
        }
      } catch (error) {
        const canRestart = error.message === 'changed_error' && this.attempt?.status === 'checkout_ready';
        if (this.newCheckout) this.newCheckout.hidden = !canRestart;
        this.status.textContent = this.config.messages[canRestart ? 'changed_ready' : error.message] || this.config.messages.network_error;
        this.status.focus();
      } finally {
        window.clearTimeout(timer);
        this.busy = false;
        if (this.trigger) {
          this.trigger.disabled = false;
          this.trigger.textContent = this.attempt ? this.config.messages.retry : this.initialLabel;
          this.trigger.removeAttribute('aria-busy');
          this.nativeForm?.querySelector('service-body-vehicle')?.sync();
        }
      }
    }

    showResult(result) {
      if (this.newCheckout) this.newCheckout.hidden = true;
      this.status.textContent = result.status === 'error' && typeof result.message === 'string'
        ? result.message
        : this.config.messages[result.status] || this.config.messages.network_error;
      this.reference.textContent = `${this.config.messages.reference} ${result.reference || this.attempt.id}`;
      this.reference.hidden = false;
      this.status.focus();
    }
  });
}


